import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { TIERS } from "../data/broker";

/**
 * ConnectionPickerDialog
 *   Opens when the user wants to add a connection from `sourceContact`.
 *   Presents a searchable list of every other contact and, on select,
 *   invokes `onPick(peerContact)`.
 */
export default function ConnectionPickerDialog({
  open,
  sourceContact,
  contacts,
  existingPeerIds, // set of contact ids already connected to source
  onCancel,
  onPick,
}) {
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setQ("");
      setBusyId(null);
      setErr("");
    }
  }, [open]);

  const candidates = useMemo(() => {
    if (!sourceContact) return [];
    const query = q.trim().toLowerCase();
    return contacts
      .filter((c) => c.id !== sourceContact.id)
      .filter((c) => {
        if (!query) return true;
        return (
          (c.name || "").toLowerCase().includes(query) ||
          (c.company_name || "").toLowerCase().includes(query) ||
          (c.state || "").toLowerCase().includes(query) ||
          (c.email || "").toLowerCase().includes(query)
        );
      })
      .slice(0, 60);
  }, [contacts, q, sourceContact]);

  const submit = async (peer) => {
    setErr("");
    setBusyId(peer.id);
    try {
      await onPick(peer);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not create link.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onCancel() : null)}>
      <DialogContent
        className="!bg-[#0a0a0c] !border-white/10 !text-zinc-100 !max-w-lg"
        data-testid="connection-picker"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight text-zinc-50">
            Add Connection
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-[12px]">
            Link{" "}
            <span className="text-cyan-300">
              {sourceContact?.name ||
                `${sourceContact?.first_name || ""} ${sourceContact?.last_name || ""}`.trim() ||
                "this contact"}
            </span>{" "}
            to another contact in the network.
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-2">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search name / company / state / email…"
            className="w-full pl-8 pr-2 py-2 bg-[#0d0d10] border border-white/10 focus:border-cyan-400/50 outline-none font-mono text-[12px] text-zinc-200 placeholder:text-zinc-600"
            data-testid="connection-picker-search"
          />
        </div>

        {err && (
          <div className="border border-rose-500/50 bg-rose-500/10 text-rose-200 px-3 py-2 font-mono text-[10px] tracking-[0.16em] uppercase mb-2">
            {err}
          </div>
        )}

        <div className="max-h-[420px] overflow-y-auto border border-white/10 divide-y divide-white/5">
          {candidates.length === 0 && (
            <div className="px-3 py-8 text-center font-mono text-[10px] text-zinc-500 tracking-[0.18em] uppercase">
              No matching contacts
            </div>
          )}
          {candidates.map((c) => {
            const tier = TIERS[c.tier] || TIERS.t5;
            const already = existingPeerIds?.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                disabled={already || busyId === c.id}
                onClick={() => submit(c)}
                data-testid={`connection-pick-${c.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                  already
                    ? "bg-white/[0.02] opacity-60 cursor-not-allowed"
                    : "hover:bg-white/[0.04]"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: tier.color, boxShadow: `0 0 8px ${tier.glow}` }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-100 text-[12px] truncate">
                      {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                    </span>
                    <span
                      className="font-mono text-[9px] tracking-[0.16em] uppercase"
                      style={{ color: tier.color }}
                    >
                      {tier.short}
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {c.company_name}
                    {c.state && <span className="text-zinc-600"> · {c.state}</span>}
                  </div>
                </div>
                <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-zinc-500">
                  {already ? "linked" : busyId === c.id ? "…" : "+"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end pt-3 mt-2 border-t border-white/10">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-white/10 hover:border-white/30 text-zinc-300 font-mono text-[11px] uppercase tracking-[0.16em]"
            data-testid="connection-picker-close"
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
